import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { CidadeSearch } from './RotaTerrestreField';

interface Aeroporto {
  iata: string;
  nome: string;
  cidade: string;
  uf: string;
  pais: string;
  label: string;
}

interface PrecoVoo {
  fonte: string;
  origem: string;
  destino: string;
  data: string;
  menor_preco: number;
  maior_preco: number;
  media_preco: number;
  moeda?: string;
  distancia_km?: number;
  nota?: string;
  domestico?: boolean;
  dias_antecedencia?: number;
  label_antecedencia?: string;
  label_temporada?: string;
  google_flights_url?: string;
  fatores?: {
    antecedencia: number;
    temporada: number;
    dia_semana: number;
    classe: number;
  };
  ofertas?: Array<{
    preco: number;
    companhias: string[];
    duracao: string;
    paradas: number;
    classe: string;
  }>;
}

interface AeroportoSearchProps {
  value: string;
  onChange: (value: string, aeroporto?: Aeroporto) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Componente de busca de aeroporto com autocomplete
export const AeroportoSearch: React.FC<AeroportoSearchProps> = ({
  value,
  onChange,
  placeholder = '🔍 Buscar aeroporto...',
  className = '',
  disabled = false
}) => {
  const [query, setQuery] = useState(value || '');
  const [resultados, setResultados] = useState<Aeroporto[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sincronizar value externo
  useEffect(() => {
    if (value !== query && !isOpen) {
      setQuery(value || '');
    }
  }, [value]);

  const buscarAeroportos = useCallback(async (termo: string) => {
    if (termo.length < 2) {
      setResultados([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(apiUrl(`/aeroportos/buscar?q=${encodeURIComponent(termo)}`));
      if (resp.ok) {
        const data = await resp.json();
        setResultados(data);
        setIsOpen(data.length > 0);
        setSelectedIndex(-1);
      }
    } catch (err) {
      console.warn('Erro ao buscar aeroportos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val); // Atualiza valor em tempo real (sem aeroporto selecionado)

    // Debounce de 300ms para buscar
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      buscarAeroportos(val);
    }, 300);
  };

  const handleSelect = (aero: Aeroporto) => {
    const displayValue = aero.label;
    setQuery(displayValue);
    onChange(displayValue, aero);
    setIsOpen(false);
    setResultados([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || resultados.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < resultados.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : resultados.length - 1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(resultados[selectedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (resultados.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400/50 bg-white pr-7 ${className}`}
          autoComplete="off"
        />
        {loading ? (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs animate-spin">⏳</span>
        ) : query ? (
          <button
            onClick={() => { setQuery(''); onChange(''); setResultados([]); setIsOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
            type="button"
          >
            ✕
          </button>
        ) : (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 text-xs">✈️</span>
        )}
      </div>

      {/* Dropdown de resultados */}
      {isOpen && resultados.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {resultados.map((aero, idx) => (
            <button
              key={aero.iata}
              type="button"
              onClick={() => handleSelect(aero)}
              onMouseEnter={() => setSelectedIndex(idx)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors flex items-center gap-2 ${
                selectedIndex === idx ? 'bg-blue-50' : ''
              }`}
            >
              <span className="font-mono font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded text-[10px]">
                {aero.iata}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-800 truncate">
                  {aero.cidade}{aero.uf ? `/${aero.uf}` : ''}
                  {aero.pais && aero.pais !== 'Brasil' && (
                    <span className="text-gray-400 ml-1">({aero.pais})</span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 truncate">{aero.nome}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Hook para estimar preço de voo
export const useEstimarPrecoVoo = () => {
  const [loading, setLoading] = useState(false);
  const [preco, setPreco] = useState<PrecoVoo | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const estimarPreco = useCallback(async (
    origem_iata: string,
    destino_iata: string,
    data: string,
    adultos = 1,
    classe = 'ECONOMY'
  ) => {
    if (!origem_iata || !destino_iata || !data) {
      setPreco(null);
      return;
    }

    setLoading(true);
    setErro(null);
    try {
      const resp = await fetch(apiUrl('/voos/estimar-preco'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origem_iata, destino_iata, data, adultos, classe })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setPreco(data);
      } else {
        setErro('Não foi possível estimar o preço');
      }
    } catch (err) {
      setErro('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, preco, erro, estimarPreco, limparPreco: () => setPreco(null) };
};

// Componente de exibição de preço estimado
export const PrecoVooEstimado: React.FC<{
  preco: PrecoVoo | null;
  loading: boolean;
  erro: string | null;
}> = ({ preco, loading, erro }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-blue-500 animate-pulse mt-1">
        <span className="animate-spin">⏳</span> Buscando preços de passagens...
      </div>
    );
  }

  if (erro) {
    return (
      <div className="text-[10px] text-red-400 mt-1">⚠️ {erro}</div>
    );
  }

  if (!preco) return null;

  const formatMoney = (v: number) => 
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="mt-1.5 rounded-lg p-2.5 border text-[10px] bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-bold text-gray-700 flex items-center gap-1">
          💰 Estimativa de Passagem
          <span className="bg-blue-100 text-blue-700 px-1.5 rounded text-[8px] font-bold">ESTIMATIVA</span>
        </span>
        {preco.distancia_km && (
          <span className="text-gray-400 bg-white/60 px-1.5 py-0.5 rounded">📏 {preco.distancia_km.toLocaleString('pt-BR')} km</span>
        )}
      </div>

      {/* Faixa de preço principal */}
      <div className="bg-white/70 rounded-lg p-2 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <div>
            <span className="text-gray-500">De </span>
            <span className="font-bold text-green-700 text-sm">{formatMoney(preco.menor_preco)}</span>
          </div>
          <span className="text-gray-300">→</span>
          <div>
            <span className="text-gray-500">Até </span>
            <span className="font-bold text-red-600 text-sm">{formatMoney(preco.maior_preco)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-gray-400">Média</div>
          <div className="font-bold text-blue-700 text-sm">{formatMoney(preco.media_preco)}</div>
        </div>
      </div>

      {/* Labels de antecedência e temporada */}
      {(preco.label_antecedencia || preco.label_temporada) && (
        <div className="space-y-0.5">
          {preco.label_antecedencia && (
            <div className="bg-white/60 rounded px-2 py-1 text-gray-700">
              {preco.label_antecedencia}
              {preco.dias_antecedencia !== undefined && (
                <span className="text-gray-400 ml-1">({preco.dias_antecedencia} dias)</span>
              )}
            </div>
          )}
          {preco.label_temporada && (
            <div className="bg-white/60 rounded px-2 py-1 text-gray-700">
              {preco.label_temporada}
            </div>
          )}
        </div>
      )}

      {/* Botão Google Flights */}
      {preco.google_flights_url && (
        <a
          href={preco.google_flights_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-2 px-3 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm text-[11px] no-underline"
        >
          🔍 Ver preços reais no Google Flights
          <span className="text-blue-200 text-[9px]">↗</span>
        </a>
      )}

      {preco.nota && (
        <div className="text-[9px] text-gray-400 italic leading-tight">{preco.nota}</div>
      )}
    </div>
  );
};

type ModoViagem = 'idavolta' | 'ida' | 'multi';

// Componente compartilhado de Rotas com cotação automática
export const RotasField: React.FC<{
  rotasAtuais: any[];
  campoId: string;
  updateFieldValue: (campoId: string, valor: any) => void;
  /** Roteiro terrestre (ônibus, Viagens 29/10/2026): mesmo layout, mas busca cidade em vez de aeroporto e não estima preço de voo. */
  terrestre?: boolean;
}> = ({ rotasAtuais, campoId, updateFieldValue, terrestre = false }) => {
  const [precos, setPrecos] = useState<Record<string, any>>({});
  const [loadings, setLoadings] = useState<Record<string, boolean>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const fetchedRef = useRef<Record<string, boolean>>({});

  const extrairIata = (valor: string) => {
    if (!valor) return '';
    const match = valor.match(/^([A-Z]{3})\s*-/);
    return match ? match[1] : '';
  };

  const estimarPrecoTrecho = useCallback(async (origemIata: string, destinoIata: string, data: string, chave: string) => {
    if (fetchedRef.current[chave]) return;
    fetchedRef.current[chave] = true;
    setLoadings(prev => ({ ...prev, [chave]: true }));
    setErros(prev => ({ ...prev, [chave]: '' }));
    try {
      const resp = await fetch(apiUrl('/voos/estimar-preco'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origem_iata: origemIata, destino_iata: destinoIata, data })
      });
      const result = await resp.json();
      if (resp.ok && result.menor_preco !== undefined) {
        setPrecos(prev => ({ ...prev, [chave]: result }));
      } else {
        setErros(prev => ({ ...prev, [chave]: result.error || 'Erro ao estimar' }));
      }
    } catch {
      setErros(prev => ({ ...prev, [chave]: 'Erro de conexão' }));
    } finally {
      setLoadings(prev => ({ ...prev, [chave]: false }));
    }
  }, []);

  useEffect(() => {
    if (terrestre) return;
    rotasAtuais.forEach((trecho) => {
      const oIata = trecho.origemIata || extrairIata(trecho.origem);
      const dIata = trecho.destinoIata || extrairIata(trecho.destino);
      if (oIata && dIata && trecho.data) {
        const chave = `${oIata}_${dIata}_${trecho.data}`;
        if (!fetchedRef.current[chave]) {
          estimarPrecoTrecho(oIata, dIata, trecho.data, chave);
        }
      }
    });
  }, [rotasAtuais, estimarPrecoTrecho]);

  const getChave = (trecho: any) => {
    const o = trecho.origemIata || extrairIata(trecho.origem);
    const d = trecho.destinoIata || extrairIata(trecho.destino);
    return o && d && trecho.data ? `${o}_${d}_${trecho.data}` : '';
  };

  const handleReestimar = (trecho: any) => {
    const chave = getChave(trecho);
    if (chave) {
      delete fetchedRef.current[chave];
      setPrecos(prev => { const n = { ...prev }; delete n[chave]; return n; });
      const o = trecho.origemIata || extrairIata(trecho.origem);
      const d = trecho.destinoIata || extrairIata(trecho.destino);
      estimarPrecoTrecho(o, d, trecho.data, chave);
    }
  };

  // Modo da viagem: ida e volta, só ida ou multidestino (vários trechos encadeados).
  // Cards antigos não têm `modo` gravado; o modo é deduzido pela forma dos trechos.
  const modoGravado = (): ModoViagem | '' => {
    const comModo = rotasAtuais.find((t: any) => t.modo);
    if (comModo) return comModo.modo;
    if (rotasAtuais.length === 0) return '';
    const voltas = rotasAtuais.filter((t: any) => t.tipo === 'volta').length;
    if (rotasAtuais.length === 2 && voltas === 1 && rotasAtuais[0].tipo !== 'volta') return 'idavolta';
    if (rotasAtuais.length === 1 && voltas === 0) return 'ida';
    return 'multi';
  };
  const [modoLocal, setModoLocal] = useState<ModoViagem>(modoGravado() || 'idavolta');
  const modo: ModoViagem = modoGravado() || modoLocal;

  const novoTrecho = (tipo: 'ida' | 'volta', m: ModoViagem, extra: any = {}) => ({
    id: `rota_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    origem: '', destino: '', data: '', origemIata: '', destinoIata: '', tipo, modo: m, ...extra,
  });

  const modelo = (m: ModoViagem) => {
    if (m === 'idavolta') return [novoTrecho('ida', m), novoTrecho('volta', m)];
    if (m === 'ida') return [novoTrecho('ida', m)];
    return [novoTrecho('ida', m), novoTrecho('ida', m)];
  };

  // Enquanto o campo está vazio, mostra trechos em branco do modo escolhido;
  // o valor só é gravado quando a pessoa preenche alguma coisa.
  const vazios = React.useMemo(() => modelo(modo), [modo]);
  const lista: any[] = rotasAtuais.length > 0 ? rotasAtuais : vazios;
  const gravar = (novas: any[]) => updateFieldValue(campoId, novas);

  const trocarModo = (m: ModoViagem) => {
    if (m === modo) return;
    setModoLocal(m);
    const preenchidos = rotasAtuais.filter((t: any) => t.origem || t.destino || t.data);
    if (preenchidos.length === 0) {
      if (rotasAtuais.length > 0) gravar([]);
      return;
    }
    const primeiro = preenchidos[0];
    const base = { origem: primeiro.origem || '', origemIata: primeiro.origemIata || '', destino: primeiro.destino || '', destinoIata: primeiro.destinoIata || '' };
    if (m === 'ida') {
      gravar([novoTrecho('ida', m, { ...base, data: primeiro.data || '' })]);
    } else if (m === 'idavolta') {
      const volta = preenchidos.find((t: any, i: number) => i > 0 && t.tipo === 'volta');
      gravar([
        novoTrecho('ida', m, { ...base, data: primeiro.data || '' }),
        novoTrecho('volta', m, { origem: base.destino, origemIata: base.destinoIata, destino: base.origem, destinoIata: base.origemIata, data: volta?.data || '' }),
      ]);
    } else {
      const trechos = preenchidos.map((t: any) => ({ ...t, tipo: 'ida', modo: m }));
      if (trechos.length < 2) trechos.push(novoTrecho('ida', m, { origem: base.destino, origemIata: base.destinoIata }));
      gravar(trechos);
    }
  };

  // Ida e volta: origem/destino da volta espelham a ida.
  const alterarAeroporto = (idx: number, lado: 'origem' | 'destino', value: string, aeroporto?: Aeroporto) => {
    const novas = lista.map((t: any) => ({ ...t, modo }));
    const iataKey = lado === 'origem' ? 'origemIata' : 'destinoIata';
    novas[idx] = { ...novas[idx], [lado]: value, [iataKey]: aeroporto?.iata || (value ? novas[idx][iataKey] : '') || '' };
    if (modo === 'idavolta' && idx === 0 && novas[1]) {
      const espelho = lado === 'origem' ? 'destino' : 'origem';
      const espelhoIata = lado === 'origem' ? 'destinoIata' : 'origemIata';
      novas[1] = { ...novas[1], [espelho]: novas[0][lado], [espelhoIata]: novas[0][iataKey] };
    }
    gravar(novas);
  };

  const alterarData = (idx: number, data: string) => {
    const novas = lista.map((t: any) => ({ ...t, modo }));
    novas[idx] = { ...novas[idx], data };
    gravar(novas);
  };

  const removerTrecho = (idx: number) => gravar(lista.filter((_: any, i: number) => i !== idx));

  const adicionarDestino = () => {
    const ultimo = lista[lista.length - 1];
    gravar([
      ...lista.map((t: any) => ({ ...t, modo })),
      novoTrecho('ida', modo, { origem: ultimo?.destino || '', origemIata: ultimo?.destinoIata || '' }),
    ]);
  };

  const tituloTrecho = (trecho: any, idx: number) => {
    const icone = terrestre ? '🚌' : '✈️';
    if (modo === 'idavolta') return trecho.tipo === 'volta' ? '🔙 Volta' : `${icone} Ida`;
    if (modo === 'ida') return `${icone} Só ida`;
    return `📍 Trecho ${idx + 1}`;
  };

  const opcoesModo: Array<[ModoViagem, string]> = [
    ['idavolta', '🔁 Ida e volta'],
    ['ida', '➡️ Só ida'],
    ['multi', '📍 Multidestino'],
  ];

  return (
    <div className="space-y-3">
      <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
        {opcoesModo.map(([m, rotulo]) => (
          <button
            key={m}
            type="button"
            onClick={() => trocarModo(m)}
            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors ${modo === m ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {rotulo}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {lista.map((trecho: any, idx: number) => {
          const chave = getChave(trecho);
          const espelhado = modo === 'idavolta' && idx === 1;
          const podeRemover = modo === 'multi' && lista.length > 2;
          return (
          <div key={trecho.id || idx} className={`relative rounded-xl border-2 overflow-hidden ${trecho.tipo === 'volta' ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-white' : 'border-blue-200 bg-gradient-to-r from-blue-50 to-white'}`}>
            <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center justify-between ${trecho.tipo === 'volta' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
              <span>{tituloTrecho(trecho, idx)}</span>
              {podeRemover && (
                <button type="button" onClick={() => removerTrecho(idx)} className="text-red-400 hover:text-red-600 p-0.5 rounded hover:bg-white/50" title="Remover trecho">✕</button>
              )}
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">{terrestre ? '📍' : '🛫'} Origem</label>
                {espelhado ? (
                  <div className="w-full px-2 py-1.5 text-xs border border-gray-100 rounded-lg bg-gray-50 text-gray-500 truncate">{trecho.origem || 'mesmo destino da ida'}</div>
                ) : (
                  terrestre ? (
                  <CidadeSearch value={trecho.origem || ''} onChange={(value) => alterarAeroporto(idx, 'origem', value)} placeholder="🔍 Buscar cidade..." />
                ) : (
                  <AeroportoSearch value={trecho.origem || ''} onChange={(value, aeroporto) => alterarAeroporto(idx, 'origem', value, aeroporto)} placeholder="🔍 Buscar aeroporto..." />
                )
                )}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">{terrestre ? '📍' : '🛬'} Destino</label>
                {espelhado ? (
                  <div className="w-full px-2 py-1.5 text-xs border border-gray-100 rounded-lg bg-gray-50 text-gray-500 truncate">{trecho.destino || 'mesma origem da ida'}</div>
                ) : (
                  terrestre ? (
                  <CidadeSearch value={trecho.destino || ''} onChange={(value) => alterarAeroporto(idx, 'destino', value)} placeholder="🔍 Buscar cidade..." />
                ) : (
                  <AeroportoSearch value={trecho.destino || ''} onChange={(value, aeroporto) => alterarAeroporto(idx, 'destino', value, aeroporto)} placeholder="🔍 Buscar aeroporto..." />
                )
                )}
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">📅 {espelhado ? 'Data da volta' : modo === 'idavolta' ? 'Data da ida' : 'Data'}</label>
                <input type="date" value={trecho.data || ''} onChange={(e) => alterarData(idx, e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400/50 bg-white" />
              </div>
            </div>
            {trecho.origem && trecho.destino && (
              <div className={`px-3 pb-2 flex items-center gap-2 text-[10px] ${trecho.tipo === 'volta' ? 'text-amber-600' : 'text-blue-600'}`}>
                <span className="font-bold">{trecho.origem}</span>
                <span className="flex-1 border-t-2 border-dashed border-current relative">
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-1">→</span>
                </span>
                <span className="font-bold">{trecho.destino}</span>
                {trecho.data && <span className="ml-2 text-gray-400">({new Date(trecho.data + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
              </div>
            )}
            {chave && !terrestre && (
              <div className="px-3 pb-2">
                <PrecoVooEstimado preco={precos[chave] || null} loading={loadings[chave] || false} erro={erros[chave] || ''} />
                {precos[chave] && !loadings[chave] && (
                  <button type="button" onClick={() => handleReestimar(trecho)} className="mt-1 text-[9px] text-gray-400 hover:text-blue-500 underline transition-colors">
                    🔄 Atualizar cotação
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
      {modo === 'multi' && (
        <button onClick={adicionarDestino} type="button" className="w-full px-3 py-2 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl text-xs font-medium transition-colors">
          📍 + Adicionar destino
        </button>
      )}
      {lista.filter((t: any) => t.origem && t.destino).length > 1 && (
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-3 border border-slate-200">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">📋 Resumo da Viagem</div>
          <div className="space-y-1">
            {lista.filter((t: any) => t.origem && t.destino).map((t: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${t.tipo === 'volta' ? 'bg-amber-500' : 'bg-blue-500'}`}>{i + 1}</span>
                <span className="text-gray-700">{t.origem}</span>
                <span className="text-gray-400">→</span>
                <span className="text-gray-700">{t.destino}</span>
                {t.data && <span className="text-gray-400 ml-auto text-[10px]">{new Date(t.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AeroportoSearch;
